import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Content,
  GenerateButton,
  PageContainer,
  PhotoItem,
  PhotoList,
  RemovePhotoButton,
  TitleContainer,
} from './EditInfluencerPage.styles';
import { Text14Regular, Text16Bold, Text24Bold } from '../../../components/utils/Text/Text.styles';
import { ErrorContainer, Spinner, UploadMediaButtonAlt } from '../../../components';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { selectChatterActionsPending, selectSelectedInfluencer } from '../../../store/slices/chatter/slice';
import { generateInfluencerPersonality } from '../../../store/slices/chatter/asyncThunks';
import { UPLOAD_FILE_CONSTRAINTS } from '../../../utils/constants';
import { notifyError, notifyInfo } from '../../../utils/notify';
import VoiceSection from './VoiceSection/VoiceSection';
import QuestionnaireSection from './QuestionnaireSection/QuestionnaireSection';
import PricingSection from './PricingSection/PricingSection';

const MAX_PERSONALITY_PHOTOS = 5;

const isValidPhoto = file =>
  file.size < UPLOAD_FILE_CONSTRAINTS.PHOTO.MAX_SIZE_IN_BYTES &&
  UPLOAD_FILE_CONSTRAINTS.PHOTO.ALLOWED_EXTENSIONS.includes(file.type.split('/')[1]);

const EditInfluencerPage = () => {
  const dispatch = useDispatch();
  const influencer = useSelector(selectSelectedInfluencer);
  const { fetchInfluencerById: fetchInfluencerByIdPending, generateInfluencerPersonality: generatePending } =
    useSelector(selectChatterActionsPending);
  const [photos, setPhotos] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [isPersonalityGenerated, setIsPersonalityGenerated] = useState(false);

  const onAddPhotos = media => {
    const validMedia = media.filter(({ file }) => isValidPhoto(file));
    const availableSlots = MAX_PERSONALITY_PHOTOS - photos.length;
    if (validMedia.length > availableSlots) {
      notifyError(`You can upload up to ${MAX_PERSONALITY_PHOTOS} photos.`);
    }
    setPhotos(prev => [...prev, ...validMedia.slice(0, availableSlots)]);
  };

  const onRemovePhoto = index => {
    URL.revokeObjectURL(photos[index].url);
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const onGenerate = () => {
    dispatch(generateInfluencerPersonality({ influencerId: influencer.id, photos: photos.map(({ file }) => file) }))
      .unwrap()
      .then(() => {
        setIsPersonalityGenerated(true);
        setPhotos([]);
        setUploadErrors([]);
        notifyInfo('Personality generated successfully!');
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const renderPersonality = () => {
    if (isPersonalityGenerated) {
      return <Text14Regular>Personality has been generated and saved.</Text14Regular>;
    }

    if (influencer.hasPersonality) {
      return <Text14Regular>Personality is already set for this creator.</Text14Regular>;
    }

    return (
      <>
        <Text14Regular>
          Upload 1 to {MAX_PERSONALITY_PHOTOS} photos of the creator. Appearance will be generated from them and saved
          as a part of the personality.
        </Text14Regular>
        <PhotoList>
          {photos.map((photo, index) => (
            <PhotoItem key={photo.url}>
              <img src={photo.url} alt="" />
              {!generatePending && (
                <RemovePhotoButton type="button" onClick={() => onRemovePhoto(index)}>
                  <CloseIcon />
                </RemovePhotoButton>
              )}
            </PhotoItem>
          ))}
          {photos.length < MAX_PERSONALITY_PHOTOS && (
            <UploadMediaButtonAlt
              type={UPLOAD_FILE_CONSTRAINTS.TYPE.PHOTO}
              onChange={onAddPhotos}
              setError={setUploadErrors}
              disabled={generatePending}
              multiple
              title="Add Photos"
            />
          )}
        </PhotoList>
        {!!uploadErrors.length && (
          <ErrorContainer
            errorText={`Some files were skipped. Allowed formats are ${UPLOAD_FILE_CONSTRAINTS.PHOTO.ALLOWED_EXTENSIONS_TEXT} up to ${UPLOAD_FILE_CONSTRAINTS.PHOTO.MAX_SIZE_IN_MB}MB.`}
          />
        )}
        <GenerateButton
          title="Generate"
          onClick={onGenerate}
          disabled={!photos.length}
          isLoading={generatePending}
          type="button"
        />
      </>
    );
  };

  return (
    <PageContainer>
      <TitleContainer>
        <Text24Bold>Edit</Text24Bold>
      </TitleContainer>
      <Content>
        {fetchInfluencerByIdPending || !influencer ? (
          <Spinner width={100} height={100} />
        ) : (
          <>
            <Card>
              <Text16Bold>Personality</Text16Bold>
              {renderPersonality()}
            </Card>
            <Card>
              <Text16Bold>Voice</Text16Bold>
              <VoiceSection influencer={influencer} />
            </Card>
            <Card>
              <Text16Bold>Questionnaire</Text16Bold>
              <QuestionnaireSection influencer={influencer} />
            </Card>
            <Card>
              <Text16Bold>Pricing</Text16Bold>
              <PricingSection influencer={influencer} />
            </Card>
          </>
        )}
      </Content>
    </PageContainer>
  );
};

export default EditInfluencerPage;
